export const SUPPORT_STAFF_ROLE_OPTIONS = [
  { label: "Outreach CHW", value: "outreach_chw" },
  { label: "HIV/Transvision CHW", value: "hiv_transvision_chw" },
  { label: "LVN", value: "lvn" },
  { label: "RN", value: "rn" },
];

export const SUPPORT_STAFF_ASSIGN_FIELD_PREFIX = "assign_support_staff_";

export const getSupportStaffRoleLabel = (value) =>
  SUPPORT_STAFF_ROLE_OPTIONS.find((option) => option.value === value)?.label ||
  value ||
  "";

export const isSupportStaffRoleCode = (role) => {
  const normalized = String(role || "").toLowerCase().replace(/\s+/g, "_");
  return SUPPORT_STAFF_ROLE_OPTIONS.some((option) => option.value === normalized);
};

/** Maps participant/member role to mentor-details `role` query (field-staff API). */
export const resolveFieldStaffProfileViewRole = (role, membersTab) => {
  const normalizedRole = String(role || "").toLowerCase().replace(/\s+/g, "_");
  const normalizedTab = String(membersTab || "").toLowerCase();

  if (
    normalizedRole === "support_staff" ||
    normalizedTab === "support_staff" ||
    isSupportStaffRoleCode(normalizedRole)
  ) {
    return "support_staff";
  }
  if (normalizedRole === "dental_assistant") return "medical_assistant";
  return normalizedRole;
};

export const getSupportStaffParticipantRowId = (row) => {
  const staffId = row?.id ?? row?.staff_id ?? "unknown";
  const role = row?.role ?? "role";
  return `${staffId}-${role}`;
};

export const getSupportStaffRoleFromAssignField = (fieldName) => {
  if (
    typeof fieldName !== "string" ||
    !fieldName.startsWith(SUPPORT_STAFF_ASSIGN_FIELD_PREFIX)
  ) {
    return null;
  }
  const role = fieldName.slice(SUPPORT_STAFF_ASSIGN_FIELD_PREFIX.length);
  return role || null;
};

export const normalizeSupportStaffRoles = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const roles = [];
  value.forEach((item) => {
    const role = typeof item === "string" ? item : item?.value;
    if (!role || seen.has(role)) return;
    seen.add(role);
    roles.push(role);
  });
  return roles;
};

const mergeStaffById = (staffList = []) => {
  const byId = new Map();
  staffList.forEach((staff) => {
    const id = staff?.staff_id ?? staff?.id;
    if (id == null || id === "") return;
    if (!byId.has(id)) {
      byId.set(id, {
        id,
        staff_id: id,
        email: staff?.email || "",
        first_name: staff?.first_name || "",
        last_name: staff?.last_name || "",
        full_name:
          staff?.full_name ||
          `${staff?.first_name || ""} ${staff?.last_name || ""}`.trim(),
      });
    }
  });
  return Array.from(byId.values());
};

const isFlatSupportStaffAssignment = (item) =>
  Boolean(
    item?.role &&
      (item?.staff_id != null || item?.id != null) &&
      !Array.isArray(item?.assigned_staff)
  );

export const buildSupportStaffApiPayload = (formValues) => {
  const needed = formValues?.support_staff_needed === true;
  if (!needed) {
    return {
      support_staff_needed: false,
      support_staff_needed_by_role: {},
      support_staff_assignments: [],
    };
  }

  const selectedRoles = normalizeSupportStaffRoles(formValues?.support_staff_roles);
  const uiRows = normalizeSupportStaffAssignments(
    formValues?.support_staff_assignments
  );
  const neededByRole = {};

  selectedRoles.forEach((role) => {
    neededByRole[role] = 0;
  });

  uiRows.forEach((row) => {
    const count =
      row.count_needed === "" ? 0 : Number.parseInt(row.count_needed, 10) || 0;
    neededByRole[row.role] = count;
  });

  const supportStaffAssignments = [];
  uiRows.forEach((row) => {
    (row.assigned_staff || []).forEach((staff) => {
      const staffId = staff?.staff_id ?? staff?.id;
      if (staffId == null || staffId === "") return;
      supportStaffAssignments.push({
        role: row.role,
        staff_id: staffId,
      });
    });
  });

  return {
    support_staff_needed: true,
    support_staff_needed_by_role: neededByRole,
    support_staff_assignments: supportStaffAssignments,
  };
};

export const mapSupportStaffDetailToForm = (detail = {}) => {
  const needed =
    detail?.support_staff_needed === true ||
    detail?.support_staff_needed === "true" ||
    detail?.support_staff_needed === 1 ||
    detail?.support_staff_needed === "1";

  let neededByRole = detail?.support_staff_needed_by_role || {};
  if (typeof neededByRole === "string") {
    try {
      neededByRole = JSON.parse(neededByRole);
    } catch {
      neededByRole = {};
    }
  }

  const byRole = detail?.support_staff_by_role || {};
  let flatAssignments = detail?.support_staff_assignments || [];
  if (typeof flatAssignments === "string") {
    try {
      flatAssignments = JSON.parse(flatAssignments);
    } catch {
      flatAssignments = [];
    }
  }

  const roles = normalizeSupportStaffRoles([
    ...Object.keys(neededByRole || {}).filter(
      (role) => Number(neededByRole[role]) > 0
    ),
    ...Object.keys(byRole).filter(
      (role) => Array.isArray(byRole[role]) && byRole[role].length > 0
    ),
    ...(Array.isArray(flatAssignments)
      ? flatAssignments.map((item) => item?.role).filter(Boolean)
      : []),
  ]);

  const assignments = roles.map((role) => {
    const staffFromByRole = (byRole[role] || []).map((staff) => ({
      id: staff?.staff_id ?? staff?.id,
      staff_id: staff?.staff_id ?? staff?.id,
      email: staff?.email || "",
      full_name:
        staff?.full_name ||
        `${staff?.first_name || ""} ${staff?.last_name || ""}`.trim(),
      first_name: staff?.first_name || "",
      last_name: staff?.last_name || "",
    }));

    const staffFromFlat = (Array.isArray(flatAssignments) ? flatAssignments : [])
      .filter((item) => item?.role === role && isFlatSupportStaffAssignment(item))
      .map((item) => ({
        id: item?.staff_id ?? item?.id,
        staff_id: item?.staff_id ?? item?.id,
      }));

    const countValue = neededByRole?.[role];
    return {
      role,
      count_needed:
        countValue === 0 || countValue ? String(countValue) : "",
      assigned_staff: mergeStaffById([...staffFromByRole, ...staffFromFlat]),
    };
  });

  return {
    support_staff_needed: needed,
    support_staff_roles: roles,
    support_staff_assignments: assignments,
  };
};

export const staffQualifiesForSupportRole = (staff, roleValue) => {
  const qualifiedRoles = Array.isArray(staff?.qualified_roles)
    ? staff.qualified_roles
    : Array.isArray(staff?.support_staff_roles)
    ? staff.support_staff_roles
    : [];

  if (!qualifiedRoles.length) return true;

  return qualifiedRoles.some(
    (item) => (typeof item === "string" ? item : item?.value) === roleValue
  );
};

export const normalizeSupportStaffAssignments = (value) => {
  if (!Array.isArray(value)) return [];

  const mergedByRole = new Map();

  const upsertRow = (role, partialRow) => {
    if (!role) return;
    const existing = mergedByRole.get(role) || {
      role,
      count_needed: "",
      assigned_staff: [],
    };

    if (
      partialRow?.count_needed === 0 ||
      (partialRow?.count_needed !== undefined &&
        partialRow?.count_needed !== "")
    ) {
      existing.count_needed = String(partialRow.count_needed);
    }

    existing.assigned_staff = mergeStaffById([
      ...existing.assigned_staff,
      ...(partialRow?.assigned_staff || []),
    ]);
    mergedByRole.set(role, existing);
  };

  value.forEach((row) => {
    const role = typeof row?.role === "string" ? row.role : row?.value;
    if (!role) return;

    if (isFlatSupportStaffAssignment(row)) {
      upsertRow(role, {
        assigned_staff: [
          {
            id: row?.staff_id ?? row?.id,
            staff_id: row?.staff_id ?? row?.id,
          },
        ],
      });
      return;
    }

    const assigned = Array.isArray(row?.assigned_staff) ? row.assigned_staff : [];
    upsertRow(role, {
      count_needed:
        row?.count_needed === 0 || row?.count_needed
          ? String(row.count_needed)
          : "",
      assigned_staff: assigned
        .map((staff) => {
          if (staff == null || staff === "") return null;
          if (typeof staff !== "object") {
            return { id: staff, staff_id: staff, full_name: String(staff) };
          }
          return {
            id: staff?.staff_id ?? staff?.id,
            staff_id: staff?.staff_id ?? staff?.id,
            first_name: staff.first_name || "",
            last_name: staff.last_name || "",
            email: staff.email || "",
            full_name:
              staff.full_name ||
              `${staff.first_name || ""} ${staff.last_name || ""}`.trim(),
          };
        })
        .filter(Boolean),
    });
  });

  return Array.from(mergedByRole.values());
};
